<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.TRANSACTIONS.DETAIL.SHOW"
    :handler="() => $refs.modal.show()"
  />

  <DialogComponent
    ref="modal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <div
      class="w-full h-full flex flex-col overflow-auto p-4 gap-2"
      :class="{ shimmerContainer: loading }"
    >
      <div class="py-4 border-b border-dashed border-[#DCD2F8]">
        <div class="flex flex-col items-center gap-3">
          <div class="flex items-center gap-2">
            <div class="w-18 h-18 rounded-full p-2 shimmerTarget relative">
              <ImgComponent
                :src="transaction?.logo"
                alt=""
                class="w-full h-full rounded-full"
              />
              <div
                class="w-7 h-7 absolute bottom-0 right-0 rounded-full p-1 shimmerTarget"
                :class="{
                  'bg-[#27BE43]': transaction?.operation == 'fund',
                  'bg-[#FF4F6C]': transaction?.operation == 'withdraw'
                }"
              >
                <ColorImg
                  src="/images/icons/arrow-up.svg"
                  alt=""
                  :class="{
                    'rotate-30': transaction?.operation == 'fund',
                    'rotate-210': transaction?.operation == 'withdraw'
                  }"
                  class="w-full h-full rounded-full bg-white"
                />
              </div>
            </div>
            <div class="font-semibold text-2xl shimmerTarget min-w-24 title-color">
              {{ transaction?.operationNameDisplay }}
            </div>
          </div>
          <div>
            <div
              class="min-w-16 shimmerTarget text-5xl font-bold"
              :class="{
                'text-[#27BE43]': transaction?.operation == 'fund',
                'text-[#FF4F6C]': transaction?.operation == 'withdraw'
              }"
            >
              {{ formatAmount(transaction?.amountToReceive) }} CFA
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4 text-lg py-5">
        <div class="flex justify-between">
          <div>Client</div>
          <div class="capitalize font-bold title-color">
             {{ $stores.user?.state?.current?.firstName }}
                {{ $stores.user?.state?.current?.lastName }}
          </div>
        </div>

        <div class="flex justify-between">
          <div>Email</div>
          <div class=" font-bold title-color">
            {{ $stores.user?.state?.current?.email }}
          </div>
        </div>

        <div class="flex justify-between">
          <div>Pays</div>
          <div class="capitalize font-bold title-color">
             {{
            Countries.find($stores.user?.state?.current?.countryCode)
              ?.countryName
          }}
          </div>
        </div>

        <div class="h-px bg-slate-100 my-1"></div>
        <!-- service -->
        <div class="flex justify-between">
          <div>Service</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.service }}
          </div>
        </div>

        <!-- collect status -->
        <div class="flex justify-between">
          <div>{{ transaction?.operationDetails?.collectName }}</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.collectStatusDisplay }}
          </div>
        </div>

        <!-- deposit status -->
        <div class="flex justify-between">
          <div>{{ transaction?.operationDetails?.depositName }}</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.depositStatusDisplay }}
          </div>
        </div>

        <div class="h-px bg-slate-100 my-1"></div>

        <!-- montant envoyé -->
        <div class="flex justify-between">
          <div>Montant envoyé</div>
          <div class="font-bold title-color">
            {{ formatAmount(transaction?.amountToSend) }} CFA
            <span v-if="getUsdtAmount('src')" class="text-sm text-slate-500">
              ({{ getUsdtAmount('src') }} USDT)
            </span>
          </div>
        </div>

        <!-- montant reçu -->
        <div class="flex justify-between">
          <div>Montant reçu</div>
          <div class="font-bold title-color">
            {{ formatAmount(transaction?.amountToReceive) }} CFA
            <span v-if="getUsdtAmount('dst')" class="text-sm text-slate-500">
              ({{ getUsdtAmount('dst') }} USDT)
            </span>
          </div>
        </div>

        <!-- frais détaillés -->
        <template v-if="hasFees">
          <div class="flex justify-between text-sm">
            <div class="text-slate-500">Frais service source</div>
            <div class="font-semibold text-slate-700">
              {{ formatAmount(transaction?.feeBreakdown?.srcServiceFee) }} CFA
            </div>
          </div>
          <div class="flex justify-between text-sm">
            <div class="text-slate-500">Frais service destination</div>
            <div class="font-semibold text-slate-700">
              {{ formatAmount(transaction?.feeBreakdown?.dstServiceFee) }} CFA
            </div>
          </div>
          <div class="flex justify-between text-sm">
            <div class="text-slate-500">Frais processeur source</div>
            <div class="font-semibold text-slate-700">
              {{ formatAmount(transaction?.feeBreakdown?.srcProcessorFee) }} CFA
            </div>
          </div>
          <div class="flex justify-between text-sm">
            <div class="text-slate-500">Frais processeur destination</div>
            <div class="font-semibold text-slate-700">
              {{ formatAmount(transaction?.feeBreakdown?.dstProcessorFee) }} CFA
            </div>
          </div>
          <div class="flex justify-between">
            <div class="font-semibold text-slate-600">Total frais</div>
            <div class="font-bold text-red-500">
              {{ formatAmount(transaction?.fee) }} CFA
            </div>
          </div>
        </template>

        <!-- bonus -->
        <div
          v-if="transaction?.bonus"
          class="flex justify-between"
        >
          <div>Bonus</div>
          <div class="font-bold text-green-500">
            +{{ formatAmount(transaction?.bonus) }} CFA
          </div>
        </div>

        <div class="h-px bg-slate-100 my-1"></div>

        <!-- date -->
        <div class="flex justify-between">
          <div>Date & heure</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.startDate }}
          </div>
        </div>

        <!-- bookmaker id -->
        <div
          class="flex justify-between"
          v-if="transaction?.bookmakerId"
        >
          <div>Id {{ transaction?.service }}</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.bookmakerId }}
          </div>
        </div>

        <!-- phone src -->
        <div
          class="flex justify-between"
          v-if="transaction?.rawData?.src?.phoneNumber || transaction?.senderPhoneNumber"
        >
          <div>Téléphone</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.rawData?.src?.phoneNumber || transaction?.senderPhoneNumber }}
          </div>
        </div>

        <!-- phone dst -->
        <div
          class="flex justify-between"
          v-if="transaction?.rawData?.dst?.phoneNumber || transaction?.recipientPhoneNumber"
        >
          <div>Destinataire</div>
          <div class="capitalize font-bold title-color">
            {{ transaction?.rawData?.dst?.phoneNumber || transaction?.recipientPhoneNumber }}
          </div>
        </div>

        <!-- référence -->
        <div class="flex justify-between">
          <div>Référence</div>
          <div
            class="font-bold title-color flex gap-1 items-center ml-auto w-[70%]"
            @click="copyReference"
          >
            <div class="line-clamp-1 pl-5">
              {{ transaction?.id }}
            </div>
            <div>
              <div class="w-6 h-6 min-w-6 -mt-1">
                <ColorImg
                  src="/images/icons/copy.svg"
                  alt=""
                  class="w-full h-full bg-primary"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- message d'erreur -->
        <div
          v-if=" (transaction?.message || transaction?.reason)"
          class="p-4 bg-red-50 rounded-2xl mt-4 border border-red-100"
        >
          <div class="text-red-500 font-semibold text-sm mb-1 uppercase tracking-wider">
            Note
          </div>
          <div class="text-red-900 font-bold leading-tight">
            {{ transaction?.message || transaction?.reason }}
          </div>
        </div>
      </div>
      <!-- {{ transaction }} -->
    </div>
  </DialogComponent>
</template>

<script>
import Commons from '@/app/features/shared/libraries/commons';
import { Countries } from '@/app/features/shared/libraries/countries';

export default {
  name: 'TransactionDetailModal',

  setup() {
    return { Commons , Countries};
  },

  computed: {
    transaction() {
      return this.$stores.transactions?.state?.detail?.data;
    },
    loading() {
      return this.$stores.transactions?.state?.detail.loading;
    },
    hasFees() {
      const fb = this.transaction?.feeBreakdown;
      return (
        fb?.srcServiceFee ||
        fb?.dstServiceFee ||
        fb?.srcProcessorFee ||
        fb?.dstProcessorFee ||
        this.transaction?.fee > 0
      );
    }
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseInt(amount).toLocaleString('fr-FR');
    },

    getUsdtAmount(side) {
      const rawData = this.transaction?.rawData;
      if (!rawData) return null;

      const usdtData = rawData[side]?.data;
      if (usdtData?.usdtAmount) {
        return usdtData.usdtAmount;
      }
      return null;
    },

    copyReference() {
      Commons.copyToClipboard(
        this.transaction?.id,
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence copiée',
            body: this.transaction?.id
          });
        },
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence non copiée',
            body: 'veuillez réessayer'
          });
        }
      );
    }
  }
};
</script>
